/**
 * Twelve month-end bars (TMMAPP-0151 §F 18, §G 21; prototype P:557, P:584): an inline SVG on tokens,
 * no chart library. The last twelve points of the served `balanceSeries`, oldest first, each bar's
 * height its balance's size against the largest in the window (at least 2 px), positive bars sage and
 * negative bars rose. A shorter series leaves the earliest slots empty; nothing is filled in.
 *
 * The bars paint the tints' `line` (the measured chart role, 3:1 non-text on the card), not their
 * `soft`, which would fade into the card. With `labels` it is the envelope page's chart: month names
 * through `format.ts` under the bars and a text summary for assistive tech. Without, it is the card's
 * strip, an image named by its first and last month-end balances.
 */
import { Box, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import type { BalancePoint } from '@/api/envelopes';
import {
  formatCivilMonth,
  formatCivilMonthName,
  formatList,
  formatMoneyMinor,
} from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { tintFor } from '@/theme/categorical';
import { resolveTokens } from '@/theme/theme';

const SLOTS = 12;

interface BalanceBarsProps {
  series: readonly BalancePoint[];
  /** The virtual account owner's currency (the summary's amounts). */
  currency: string | null;
  /** The drawing's height in px. */
  height: number;
  /** The envelope page's chart: month labels and a text summary. */
  labels?: boolean;
  /** A bar's share of its slot (the card's strip is tight, the chart airy). */
  barRatio?: number;
}

export function BalanceBars({
  series,
  currency,
  height,
  labels = false,
  barRatio = 0.8,
}: BalanceBarsProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const recent = series.slice(-SLOTS);
  const slots: (BalancePoint | null)[] = [
    ...Array.from({ length: SLOTS - recent.length }, () => null),
    ...recent,
  ];
  const largest = Math.max(1, ...recent.map((point) => Math.abs(point.balanceMinor)));
  const positive = tintFor('sage', tokens).line;
  const negative = tintFor('rose', tokens).line;
  const slotPct = 100 / SLOTS;
  const first = recent[0];
  const last = recent[recent.length - 1];

  const drawing = (
    <svg
      width="100%"
      height={height}
      // The card's strip names its first and last month-end balances (as the trend it replaced did);
      // the chart's own summary sits beside it in full.
      {...(labels || first === undefined || last === undefined
        ? { 'aria-hidden': true }
        : {
            role: 'img',
            'aria-label': t('envelope.balanceTrendLabel', {
              first: formatMoneyMinor(first.balanceMinor, currency, locale),
              last: formatMoneyMinor(last.balanceMinor, currency, locale),
            }),
          })}
      data-balance-bars=""
      style={{ display: 'block', overflow: 'visible' }}
    >
      {slots.map((point, index) => {
        if (point === null) return null;
        const barHeight = Math.max(2, (Math.abs(point.balanceMinor) / largest) * height);
        return (
          <rect
            key={point.month}
            data-month={point.month}
            data-sign={point.balanceMinor < 0 ? 'negative' : 'positive'}
            x={`${(index + (1 - barRatio) / 2) * slotPct}%`}
            width={`${barRatio * slotPct}%`}
            y={height - barHeight}
            height={barHeight}
            rx={2}
            fill={point.balanceMinor < 0 ? negative : positive}
          />
        );
      })}
    </svg>
  );

  if (!labels) return drawing;

  return (
    <Box data-balance-chart="">
      {first !== undefined && last !== undefined && (
        <Box component="p" sx={visuallyHidden}>
          {t('envelope.v2.chartSummary', {
            from: formatCivilMonth(first.month, locale),
            to: formatCivilMonth(last.month, locale),
            // The list's punctuation is the locale's (TMMAPP-0154 item 15).
            months: formatList(
              recent.map((point) =>
                t('envelope.v2.chartPoint', {
                  month: formatCivilMonthName(point.month, locale),
                  amount: formatMoneyMinor(point.balanceMinor, currency, locale),
                }),
              ),
              locale,
            ),
          })}
        </Box>
      )}
      {drawing}
      <Box
        aria-hidden
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${SLOTS}, minmax(0, 1fr))`,
          mt: '6px',
        }}
      >
        {slots.map((point, index) => (
          <Box
            component="span"
            key={point?.month ?? `empty-${index}`}
            sx={{
              fontSize: 11,
              lineHeight: '16px',
              textAlign: 'center',
              color: tokens.textLabel('surface'),
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {point === null ? '' : formatCivilMonthName(point.month, locale)}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
